import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DetailPageShell } from '../../components/listPage';
import { useSelector } from 'react-redux';
import { Icon, Loader } from 'semantic-ui-react';
import { useGetQuizAttemptQuery, useGetQuizAttemptsQuery } from '../product_knowledge/productKnowledgeApiSlice';
import { DetailSection, SectionTitle, StatusPill, cardStyle, greyBox } from '../../components/reportCards';
import { shortDate } from '../chat_sessions/MobileChatSessionList';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { NAV } from '../../navLabels';
import { typeLabel } from './QuizAttemptsList';

/**
 * One quiz attempt: the question as it was answered — for 객관식 every option with the
 * trainee's choice and the right one marked, for 서술형 their answer and the 채점 기준 —
 * then the feedback / 해설, the source, the next review, and the other tries at the same
 * question. Opened from 퀴즈 기록 and the quiz dashboard's 최근 학습 기록.
 */

const Option = ({ text, mine, correct }) => {
  const wrongPick = mine && !correct;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 14px',
        borderRadius: '12px',
        border: `1.5px solid ${correct ? '#34C759' : wrongPick ? '#FF3B30' : '#E5E5EA'}`,
        backgroundColor: correct ? '#F2FCF5' : wrongPick ? '#FFF5F5' : 'white',
      }}
    >
      <Icon
        name={correct ? 'check circle' : wrongPick ? 'times circle' : 'circle outline'}
        className="centered-icon"
        style={{ flexShrink: 0, fontSize: '16px', color: correct ? '#34C759' : wrongPick ? '#FF3B30' : '#c7c7cc' }}
      />
      <span style={{ flex: 1, fontSize: '14px', lineHeight: 1.55, color: '#1d1d1f', wordBreak: 'keep-all' }}>{text}</span>
      {(mine || correct) && (
        <span style={{ fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap', color: correct ? '#1E8E3E' : '#D93025' }}>
          {mine && correct ? '내 답 · 정답' : mine ? '내 답' : '정답'}
        </span>
      )}
    </div>
  );
};

const QuizAttemptDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const me = useSelector(state => state.auth.user);
  const isAdmin = !!(me?.is_staff || me?.is_superuser || me?.is_contractor_admin);

  const { data: a, isLoading, isError } = useGetQuizAttemptQuery(id);
  const { data: others } = useGetQuizAttemptsQuery(
    { question: a?.question, trainee: a?.user, page_size: 50, ordering: '-created_at' },
    { skip: !a },
  );

  const isMC = a?.question_type_code !== 'OE';
  const feedback = a?.grading_feedback;
  // MC feedback is "정답은 …입니다." + the 해설, so show the 해설 only when it adds something.
  const explanation = a?.explanation && !(feedback || '').includes(a.explanation.trim()) ? a.explanation : null;
  const tries = others?.results || [];

  return (
    <DetailPageShell
      isMobile={isMobile}
      title={a?.product_name || NAV.QUIZ_HISTORY}
      subtitle={a ? [shortDate(a.created_at), typeLabel(a.question_type_code), isAdmin ? a.user_display_name || a.user_username : null].filter(Boolean).join(' · ') : '퀴즈 풀이'}
      backTo="/quiz/attempts"
    >
        {isLoading ? (
          <Loader active inline="centered" style={{ marginTop: '40px' }} />
        ) : isError || !a ? (
          <div style={{ ...cardStyle, textAlign: 'center', color: '#86868b' }}>풀이 기록을 찾을 수 없습니다.</div>
        ) : (
          <>
            <div style={{ ...cardStyle, padding: isMobile ? '16px' : '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <StatusPill favorable={a.is_correct}>{a.is_correct ? '정답' : '오답'}</StatusPill>
                <span style={{ fontSize: '12px', color: '#86868b' }}>문제</span>
              </div>
              <div style={{ fontSize: '16px', fontWeight: 600, lineHeight: 1.55, color: '#1d1d1f', wordBreak: 'keep-all', whiteSpace: 'pre-line' }}>
                {a.question_text}
              </div>

              {isMC && a.options?.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
                  {a.options.map((opt, i) => (
                    <Option key={i} text={opt} mine={opt === a.user_answer} correct={opt === a.correct_answer} />
                  ))}
                </div>
              ) : (
                <>
                  <DetailSection label="내 답안">
                    <div style={{ ...greyBox, whiteSpace: 'pre-wrap', fontSize: '14px', color: '#1d1d1f' }}>{a.user_answer || '—'}</div>
                  </DetailSection>
                  {a.rubric && (
                    <DetailSection label="채점 기준">
                      <div style={{ ...greyBox, whiteSpace: 'pre-line' }}>{a.rubric}</div>
                    </DetailSection>
                  )}
                </>
              )}

              {(feedback || explanation) && (
                <DetailSection label={isMC ? '해설' : '채점 피드백'}>
                  <div style={{ borderRadius: '12px', padding: '12px 14px', fontSize: '14px', lineHeight: 1.6, wordBreak: 'keep-all', whiteSpace: 'pre-line', backgroundColor: a.is_correct ? '#F2FCF5' : '#FFF5F5', color: '#1d1d1f' }}>
                    {feedback || explanation}
                  </div>
                </DetailSection>
              )}
              {feedback && explanation && (
                <DetailSection label="해설">
                  <div style={{ ...greyBox, whiteSpace: 'pre-line' }}>{explanation}</div>
                </DetailSection>
              )}
              {a.citation_url && (
                <a href={a.citation_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '14px', fontSize: '13px', color: '#007AFF' }}>
                  <Icon name="external" className="centered-icon" style={{ fontSize: '13px' }} /> 출처: {a.citation_source || '참고 자료'}
                </a>
              )}
            </div>

            <div style={{ ...cardStyle, marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px' }}>
              <Icon name="calendar check outline" className="centered-icon" style={{ color: '#007AFF', fontSize: '15px' }} />
              <span style={{ fontSize: '14px', color: '#1d1d1f' }}>
                다음 복습 {new Date(a.next_review_at).toLocaleDateString('ko-KR')}
              </span>
              <span style={{ fontSize: '13px', color: '#86868b' }}>· 간격 {a.review_interval}일</span>
            </div>

            {tries.length > 1 && (
              <>
                <SectionTitle meta={`${tries.length}회`}>이 문제의 풀이 기록</SectionTitle>
                <div style={{ ...cardStyle, padding: '4px 16px' }}>
                  {tries.map((t, i) => {
                    const current = String(t.id) === String(a.id);
                    return (
                      <div
                        key={t.id}
                        role={current ? undefined : 'button'}
                        onClick={current ? undefined : () => navigate(`/quiz/attempts/${t.id}`, { replace: true })}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 0', borderTop: i ? '1px solid rgba(0,0,0,0.05)' : 'none', cursor: current ? 'default' : 'pointer' }}
                      >
                        <StatusPill favorable={t.is_correct}>{t.is_correct ? '정답' : '오답'}</StatusPill>
                        <span style={{ flex: 1, minWidth: 0, fontSize: '13px', color: '#424245', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.user_answer}
                        </span>
                        <span style={{ fontSize: '12px', color: current ? '#007AFF' : '#86868b', fontWeight: current ? 700 : 400, whiteSpace: 'nowrap' }}>
                          {current ? '지금 보는 기록' : shortDate(t.created_at)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {isAdmin && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px' }}>
                <Link to={`/quiz/attempts?trainee=${a.user}`} style={{ borderRadius: '999px', padding: '10px 16px', fontSize: '14px', fontWeight: 600, backgroundColor: '#F2F2F7', color: '#1d1d1f' }}>
                  이 직원의 퀴즈 기록
                </Link>
                <Link to={`/admin/quiz-questions/${a.question}`} style={{ borderRadius: '999px', padding: '10px 16px', fontSize: '14px', fontWeight: 600, backgroundColor: '#F2F2F7', color: '#1d1d1f' }}>
                  문제 관리
                </Link>
              </div>
            )}
          </>
        )}
    </DetailPageShell>
  );
};

export default QuizAttemptDetail;
